/**
 * Copyright Sourcepole AG
 * All rights reserved.
 *
 * This source code is licensed under the BSD-style license found in the
 * LICENSE file in the root directory of this source tree.
 */


import yaml from 'js-yaml';
import polygonIntersectTest from 'polygon-intersect-test';

import {LayerRole} from '../actions/layers';
import ConfigUtils from './ConfigUtils';
import CoordinatesUtils from './CoordinatesUtils';
import DataServiceExprUtils from './DataServiceExprUtils';
import IdentifyUtils from './IdentifyUtils';
import LayerUtils from './LayerUtils';
import LocaleUtils from './LocaleUtils';
import Request from './Request';


export const SearchResultType = {
    PLACE: 0,
    THEMELAYER: 1,
    THEME: 2,
    EXTERNALLAYER: 3,
    TASK: 4
};

function coordinatesSearch(text, searchParams, callback) {
    const displaycrs = searchParams.displaycrs || "EPSG:4326";
    const matches = text.replace(/[’']/g, "").match(/^\s*([+-]?\d+\.?\d*)[,\s]\s*([+-]?\d+\.?\d*)\s*$/);
    const items = [];
    if (matches && matches.length >= 3) {
        const x = parseFloat(matches[1]);
        const y = parseFloat(matches[2]);
        const searchCrs = [displaycrs, ...searchParams.extracrs.filter(crs => !['EPSG:4326', displaycrs].includes(crs))];
        searchCrs.forEach(crs => {
            items.push({
                id: "coord" + items.length,
                text: x + ", " + y + " (" + crs + ")",
                x: x,
                y: y,
                crs: crs,
                bbox: [x, y, x, y]
            });
        });
        if (x >= -180 && x <= 180 && y >= -90 && y <= 90) {
            const title = Math.abs(x) + (x >= 0 ? "°E" : "°W") + ", "
                      + Math.abs(y) + (y >= 0 ? "°N" : "°S");
            items.push({
                id: "coord" + items.length,
                text: title,
                x: x,
                y: y,
                crs: "EPSG:4326",
                bbox: [x, y, x, y]
            });
        }
        if (x >= -90 && x <= 90 && y >= -180 && y <= 180 && x !== y) {
            const title = Math.abs(y) + (y >= 0 ? "°E" : "°W") + ", "
                      + Math.abs(x) + (x >= 0 ? "°N" : "°S");
            items.push({
                id: "coord" + items.length,
                text: title,
                x: y,
                y: x,
                crs: "EPSG:4326",
                bbox: [y, x, y, x]
            });
        }
    }
    const results = [];
    if (items.length > 0) {
        results.push(
            {
                id: "coords",
                titlemsgid: LocaleUtils.trmsg("search.coordinates"),
                type: SearchResultType.PLACE,
                items: items
            }
        );
    }
    callback({results: results});
}

/** ************************************************************************ **/

class NominatimSearch {
    static TRANSLATIONS = {};

    static search(text, searchParams, callback) {
        const viewboxParams = {};
        if (searchParams.filterBBox) {
            viewboxParams.viewbox = CoordinatesUtils.reprojectBbox(searchParams.filterBBox, searchParams.mapcrs, "EPSG:4326").join(",");
            viewboxParams.bounded = 1;
        }
        Request.get("https://nominatim.openstreetmap.org/search", {params: {
            'q': text,
            'addressdetails': 1,
            'polygon_geojson': 1,
            'limit': 20,
            'format': 'json',
            'accept-language': searchParams.lang,
            ...(searchParams.cfgParams || {}),
            ...viewboxParams
        }}).then(response => {
            const locale = searchParams.lang;
            if (NominatimSearch.TRANSLATIONS[locale] === undefined) {
                NominatimSearch.TRANSLATIONS[locale] = {promise: NominatimSearch.loadLocale(locale)};
                NominatimSearch.TRANSLATIONS[locale].promise.then(() => {
                    NominatimSearch.parseResults(response.data, NominatimSearch.TRANSLATIONS[locale].strings, callback);
                });
            } else if (NominatimSearch.TRANSLATIONS[locale].promise) {
                NominatimSearch.TRANSLATIONS[locale].promise.then(() => {
                    NominatimSearch.parseResults(response.data, NominatimSearch.TRANSLATIONS[locale].strings, callback);
                });
            } else if (NominatimSearch.TRANSLATIONS[locale].strings) {
                NominatimSearch.parseResults(response.data, NominatimSearch.TRANSLATIONS[locale].strings, callback);
            }
        });
    }
    static parseResults(obj, translations, callback) {
        const results = [];
        const groups = {};
        let groupcounter = 0;

        (obj || []).map(entry => {
            if (!(entry.class in groups)) {
                let title = entry.type;
                try {
                    title = translations[entry.class][entry.type] || entry.type;
                } catch {
                    /* pass */
                }
                groups[entry.class] = {
                    id: "nominatimgroup" + (groupcounter++),
                    // capitalize class
                    title: title,
                    type: SearchResultType.PLACE,
                    items: []
                };
                results.push(groups[entry.class]);
            }

            // shorten display_name
            let text = entry.display_name.split(', ').slice(0, 3).join(', ');
            // map label
            const label = text;

            // collect address fields
            const address = [];
            if (entry.address.town) {
                address.push(entry.address.town);
            }
            if (entry.address.city) {
                address.push(entry.address.city);
            }
            if (entry.address.state) {
                address.push(entry.address.state);
            }
            if (entry.address.country) {
                address.push(entry.address.country);
            }
            if (address.length > 0) {
                text += "<br/><i>" + address.join(', ') + "</i>";
            }

            // reorder coords from [miny, maxy, minx, maxx] to [minx, miny, maxx, maxy]
            const b = entry.boundingbox.map(coord => parseFloat(coord));
            const bbox = [b[2], b[0], b[3], b[1]];

            groups[entry.class].items.push({
                id: entry.place_id,
                // shorten display_name
                text: text,
                label: label,
                bbox: bbox,
                geometry: entry.geojson,
                x: 0.5 * (bbox[0] + bbox[2]),
                y: 0.5 * (bbox[1] + bbox[3]),
                crs: "EPSG:4326",
                provider: "nominatim"
            });
        });
        callback({results: results});
    }
    static loadLocale(locale) {
        return new Promise((resolve) => {
            Request.get('https://raw.githubusercontent.com/openstreetmap/openstreetmap-website/master/config/locales/' + locale + '.yml')
                .then(resp2 => {
                    NominatimSearch.TRANSLATIONS[locale] = {strings: NominatimSearch.parseLocale(resp2.data, locale)};
                    resolve(true);
                }).catch(() => {
                    NominatimSearch.TRANSLATIONS[locale] = {
                        promise: Request.get('https://raw.githubusercontent.com/openstreetmap/openstreetmap-website/master/config/locales/' + locale.slice(0, 2) + '.yml')
                            .then(resp3 => {
                                NominatimSearch.TRANSLATIONS[locale] = {strings: NominatimSearch.parseLocale(resp3.data, locale.slice(0, 2))};
                                resolve(true);
                            }).catch(() => {
                                NominatimSearch.TRANSLATIONS[locale] = {strings: {}};
                                resolve(true);
                            })
                    };
                });
        });
    }
    static parseLocale(data, locale) {
        const doc = yaml.load(data, {json: true});
        try {
            return doc[locale].geocoder.search_osm_nominatim.prefix;
        } catch {
            return {};
        }
    }
}

/** ************************************************************************ **/

export class QgisSearch {

    static buildFilter(filter, values, removeEmptySubexpr = true) {
        if (typeof filter === "string") {
            let replacedFilter = filter;
            // Uses legacy expression format
            Object.entries(values).forEach(([key, value]) => {
                replacedFilter = replacedFilter.replaceAll(`$${key}$`, value.replace("'", "\\'"));
            });
            return replacedFilter;
        } else {
            // Data service filter expression
            let replacedExpr = DataServiceExprUtils.replaceExpressionVariables(filter, values, {});
            if (replacedExpr === null) {
                /* eslint-disable-next-line */
                console.warn("Invalid filter expression: " + JSON.stringify(filter));
                return null;
            }
            if (removeEmptySubexpr && DataServiceExprUtils.isSimpleExpr(replacedExpr)) {
                replacedExpr = DataServiceExprUtils.removeEmptySubexpressions(replacedExpr);
            }
            return DataServiceExprUtils.buildFilter(replacedExpr);
        }
    }

    static buildFeatureInfoUrlParams(searchParams, filter, values, removeEmptySubexpr = true) {
        const params = {
            SERVICE: 'WMS',
            VERSION: searchParams.theme.version,
            REQUEST: 'GetFeatureInfo',
            CRS: searchParams.theme.mapCrs,
            WIDTH: 100,
            HEIGHT: 100,
            WITH_GEOMETRY: true,
            WITH_MAPTIP: false,
            feature_count: searchParams.cfgParams.featureCount || 100,
            info_format: 'text/xml'
        };

        const newParams = {
            LAYERS: Array.from(Object.keys(filter)),
            FILTER: []
        };

        newParams.LAYERS.forEach(layer => {
            const replacedFilter = QgisSearch.buildFilter(filter[layer], values, removeEmptySubexpr);
            if (replacedFilter !== null) {
                newParams.FILTER.push(layer + ":" + replacedFilter);
            }
        });
        params.QUERY_LAYERS = params.LAYERS = newParams.LAYERS.join(",");
        params.FILTER = newParams.FILTER.join(";");

        return params;
    }

    static search(text, searchParams, callback) {
        const params = QgisSearch.buildFeatureInfoUrlParams(searchParams, {...searchParams.cfgParams.expression}, {TEXT: text}, false);
        Request.get(searchParams.theme.featureInfoUrl, {params}).then(response => {
            callback(QgisSearch.searchResults(
                IdentifyUtils.parseResponse(response.data, searchParams.theme, 'text/xml', null, searchParams.mapcrs),
                searchParams.cfgParams.title, searchParams.cfgParams.resultTitle
            ));
        }).catch(() => {
            callback({results: []});
        });
    }
    static searchResults(features, title, resultTitle) {
        const results = [];
        Object.entries(features).forEach(([layername, layerfeatures]) => {
            const items = layerfeatures.map(feature => {
                if (!feature.bbox || !feature.geometry) {
                    /* eslint-disable-next-line */
                    console.warn("Skipping result without geometry");
                    return null;
                }
                const values = {
                    ...feature.properties,
                    id: feature.id,
                    layername: layername
                };
                return {
                    id: "qgis." + layername + "." + feature.id,
                    text: resultTitle ? resultTitle.replace(/{([^}]+)}/g, match => values[match.slice(1, -1)]) : feature.displayname,
                    x: 0.5 * (feature.bbox[0] + feature.bbox[2]),
                    y: 0.5 * (feature.bbox[1] + feature.bbox[3]),
                    crs: feature.crs,
                    bbox: feature.bbox,
                    geometry: feature.geometry,
                    layername: layername
                };
            }).filter(Boolean);
            results.push(
                {
                    id: "qgis." + layername,
                    title: title + ": " + layername,
                    type: SearchResultType.PLACE,
                    items: items
                }
            );
        });
        return {results};
    }
    static getResultGeometry(resultItem, callback) {
        callback({geometry: resultItem.geometry, crs: resultItem.crs});
    }
}

/** ************************************************************************ **/

export class FulltextSearch {
    static search(searchText, searchParams, callback) {
        const searchServiceUrl = ConfigUtils.getConfigProp("searchServiceUrl");
        if (!searchServiceUrl) {
            /* eslint-disable-next-line */
            console.warn("Fulltext search failed: searchServiceUrl not set");
            callback({results: []});
        }
        // Compute search filter
        const searchFilter = new Set([...searchParams.cfgParams.default || [], ...searchParams.searchTerms]);
        const facetMap = searchParams.cfgParams.layers || {};
        searchParams.activeLayers.forEach(layername => {
            if (facetMap[layername]) {
                searchFilter.add(facetMap[layername]);
            }
        });
        const params = {
            searchtext: searchText,
            filter: [...searchFilter].join(","),
            limit: searchParams.limit
        };
        const iconPath = ConfigUtils.getAssetsPath() + '/img/search/';
        Request.get(searchServiceUrl, {params}).then(response => {
            const data = FulltextSearch.filterFulltextResults(response.data, searchParams.filterPoly, searchParams.mapcrs);
            const placeResultCount = (data.result_counts || []).reduce((res, entry) => {
                if (entry.dataproduct_id === 'dataproduct') {
                    return res;
                } else if (res === -1 || entry.count === -1) {
                    return -1;
                } else {
                    return res + entry.count;
                }
            }, 0);
            const results = [];
            // Layers
            const formatLayerEntry = (dataproduct => ({
                type: SearchResultType.THEMELAYER,
                id: dataproduct.dataproduct_id,
                text: dataproduct.display,
                thumbnail: iconPath + "dataproduct.svg",
                info: dataproduct.dset_info,
                sublayers: dataproduct.sublayers ? dataproduct.sublayers.map(formatLayerEntry) : null
            }));
            results.push({
                id: "fulltext.layers",
                titlemsgid: LocaleUtils.trmsg("search.layers"),
                type: SearchResultType.THEMELAYER,
                items: data.results.filter(entry => entry.dataproduct).map(entry => formatLayerEntry(entry.dataproduct))
            });
            // Places
            results.push({
                id: "fulltext.places",
                titlemsgid: LocaleUtils.trmsg("search.places"),
                resultCount: placeResultCount,
                type: SearchResultType.PLACE,
                items: data.results.filter(entry => entry.feature && entry.feature.bbox).map(entry => {
                    let center = entry.feature.center;
                    if (!center) {
                        center = [
                            0.5 * (entry.feature.bbox[0] + entry.feature.bbox[2]),
                            0.5 * (entry.feature.bbox[1] + entry.feature.bbox[3])
                        ];
                    }
                    return {
                        id: entry.feature.feature_id,
                        text: entry.feature.display,
                        x: center[0],
                        y: center[1],
                        crs: entry.feature.srid ? "EPSG:" + String(entry.feature.srid).replace(/^EPSG:/, '') : null,
                        bbox: entry.feature.bbox,
                        thumbnail: iconPath + entry.feature.dataproduct_id + ".svg",
                        // fulltext specific info
                        dataproduct_id: entry.feature.dataproduct_id,
                        id_field_name: entry.feature.id_field_name
                    };
                })
            });
            callback({results: results, result_counts: data.result_counts});
        }).catch(e => {
            // eslint-disable-next-line
            console.warn("Fulltext search failed: " + e);
            callback({results: []});
        });
    }
    static filterFulltextResults(data, filterPoly, mapCrs) {
        if (!filterPoly) {
            return data;
        }
        data.results = data.results.filter(result => {
            if (!result.feature || !result.feature.bbox) {
                return true;
            }
            const [xmin, ymin, xmax, ymax] = CoordinatesUtils.reprojectBbox(result.feature.bbox, "EPSG:" + result.feature.srid, mapCrs);
            const intersects = polygonIntersectTest([[xmin, ymin], [xmax, ymin], [xmax, ymax], [xmin, ymax], [xmin, ymin]], filterPoly);
            if (!intersects) {
                data.result_counts.find(entry => entry.dataproduct_id === result.feature.dataproduct_id).count -= 1;
            }
            return intersects;
        });
        return data;
    }
    static getResultGeometry(resultItem, callback) {
        const dataServiceUrl = ConfigUtils.getConfigProp("searchDataServiceUrl") || ConfigUtils.getConfigProp("dataServiceUrl");
        if (!dataServiceUrl) {
            callback(null);
        }
        // URL example: /api/data/v1/ch.so.afu.fliessgewaesser.netz/?filter=[["gewissnr","=",1179]]
        const quot = typeof(resultItem.id) === 'string' ? '"' : '';
        const filter = `[["${resultItem.id_field_name}","=", ${quot}${resultItem.id}${quot}]]`;
        Request.get(dataServiceUrl.replace(/\/?$/, "/") + resultItem.dataproduct_id + "/?filter=" + filter).then(response => {
            const feature = response.data;
            const bbox = response.data.bbox;
            callback({bbox, center: null, feature: feature, crs: feature.crs.properties.name});
        }).catch(() => {
            callback(null);
        });
    }
    static getLayerDefinition(resultItem, callback) {
        const dataProductServiceUrl = ConfigUtils.getConfigProp("dataproductServiceUrl");
        if (!dataProductServiceUrl) {
            /* eslint-disable-next-line */
            console.warn("Fulltext search: failed to get layer definition, dataproductServiceUrl is not defined");
            callback(null);
        }
        const params = {
            filter: resultItem.id
        };
        Request.get(dataProductServiceUrl.replace(/\/?$/, "/") + "weblayers", {params}).then(response => {
            callback(response.data[resultItem.id]?.[0]);
        }).catch(() => {
            callback(null);
        });
    }
    static handleHighlightParameters(hp, hf, st, callback) {
        const searchServiceUrl = ConfigUtils.getConfigProp("searchServiceUrl");
        const dataServiceUrl = ConfigUtils.getConfigProp("searchDataServiceUrl") || ConfigUtils.getConfigProp("dataServiceUrl");
        if (!searchServiceUrl || !dataServiceUrl) {
            return;
        }
        const queryFeature = (filter) => {
            Request.get(dataServiceUrl.replace(/\/?$/, "/") + hp + "/?filter=" + filter).then(response => {
                const bbox = response.data.bbox;
                const item = {
                    x: 0.5 * [bbox[0] + bbox[2]],
                    y: 0.5 * [bbox[1] + bbox[3]],
                    label: st,
                    crs: response.data.crs.properties.name,
                    bbox: bbox
                };
                callback(item, {feature: response.data, crs: response.data.crs.properties.name});
            }).catch(() => {});
        };
        if (hp && hf) {
            queryFeature(hf);
        } else if (hp && st) {
            const params = {
                searchtext: st,
                filter: hp,
                limit: 1
            };
            Request.get(searchServiceUrl, {params}).then(response => {
                if (response.data.results && response.data.results.length === 1) {
                    const result = response.data.results[0].feature;
                    const quot = typeof(result.feature_id) === 'string' ? '"' : '';
                    const filter = `[["${result.id_field_name}","=", ${quot}${result.feature_id}${quot}]]`;
                    queryFeature(filter);
                }
            }).catch(() => {});
        }
    }
}

/** ************************************************************************ **/

const SearchProviders = {
    coordinates: {
        labelmsgid: "search.coordinates",
        onSearch: coordinatesSearch,
        handlesGeomFilter: false
    },
    nominatim: {
        label: "OpenStreetMap",
        onSearch: NominatimSearch.search,
        handlesGeomFilter: false
    },
    qgis: {
        label: "QGIS",
        onSearch: QgisSearch.search,
        getResultGeometry: QgisSearch.getResultGeometry,
        handlesGeomFilter: false
    },
    fulltext: {
        label: "Fulltext",
        onSearch: FulltextSearch.search,
        getResultGeometry: FulltextSearch.getResultGeometry,
        getLayerDefinition: FulltextSearch.getLayerDefinition,
        handlesGeomFilter: true
    }
};

/** ************************************************************************ **/

const addedSearchProviders = new Set();

export function registerSearchProvider(key, config) {
    SearchProviders[key] = config;
    addedSearchProviders.add(key);
}

export function unregisterSearchProvider(key) {
    delete SearchProviders[key];
    addedSearchProviders.delete(key);
}

export function collectSearchProviders(theme, layers, mapScale = null) {
    // Collect active layers/search terms
    let searchTerms = [];
    const activeLayers = [];
    for (const entry of LayerUtils.explodeLayers(layers)) {
        if (
            entry.layer.role === LayerRole.THEME && entry.sublayer.visibility === true &&
            (mapScale !== null && LayerUtils.layerScaleInRange(entry.sublayer, mapScale))
        ) {
            searchTerms = searchTerms.concat(entry.sublayer.searchterms || []);
            activeLayers.push(entry.sublayer.name);
        }
    }

    const searchProviders = {...SearchProviders, ...window.QWC2SearchProviders || {}};
    const availableProviders = {};
    const themeLayerNames = layers.map(layer => layer.role === LayerRole.THEME ? layer.params.LAYERS : "").join(",").split(",").filter(entry => entry);
    const providerKeys = new Set();
    const enabledProviders = [...(theme?.searchProviders || []), ...addedSearchProviders];
    for (let entry of enabledProviders) {
        if (typeof entry === 'string') {
            entry = {provider: entry};
        }
        // Omit qgis provider with field configuration, this is only supported through the FeatureSearch plugin
        if (entry.provider === 'qgis' && entry?.params?.fields) {
            continue;
        }
        const provider = searchProviders[entry.provider];
        if (provider) {
            if (provider.requiresLayer && !themeLayerNames.includes(provider.requiresLayer)) {
                continue;
            }
            let key = entry.key ?? entry.provider;
            if (providerKeys.has(key)) {
                let i = 0;
                for (i = 0; providerKeys.has(key + "_" + i); ++i);
                key = key + "_" + i;
            }
            providerKeys.add(key);
            availableProviders[key] = {
                ...provider,
                label: entry.label ?? provider.label,
                labelmsgid: entry.labelmsgid ?? provider.labelmsgid,
                getResultGeometry: provider.getResultGeometry,
                cfgParams: entry.params || {},
                params: {
                    searchTerms: searchTerms,
                    activeLayers: activeLayers,
                    theme: theme
                }
            };
        }
    }
    return availableProviders;
}
